import { useEffect, useImperativeHandle } from 'react';
import { Stack, Text } from '@adc/parallax-component-library';
import { LocalDate } from '@js-joda/core';
import { useForm, useStore } from '@tanstack/react-form';

import { CLMEmailInput } from '~/components/inputs/CLMEmailInput';
import { CLMPhoneInput } from '~/components/inputs/CLMPhoneNumberInput';
import { CLMTextInput } from '~/components/inputs/CLMTextInput';
import { PatientInformationInviteLabels } from '~/features/patientCreation/constants';
import type {
  PatientInformationFormProps,
  PatientInformationInviteLabelsKeys,
} from '~/features/patientCreation/types';
import { patientInformationSchema } from '~/features/patientCreation/validations';
import { buildTestId } from '~/utils/buildTestId';
import { DEFAULT_DATE_FORMAT } from '~/utils/dateFormats';
import { getErrorProps } from '~/utils/forms/getErrorProps';

import { DatePicker } from '../DatePicker';

const [firstInviteLabelKey] = Object.keys(
  PatientInformationInviteLabels,
) as PatientInformationInviteLabelsKeys[];

export const PatientInformationForm = ({
  ref,
  onSubmit,
  setIsNextDisabled,
  defaultValues,
  testID,
}: PatientInformationFormProps) => {
  const form = useForm({
    defaultValues: {
      id: defaultValues.id ?? '',
      email: defaultValues.email ?? '',
      phone: defaultValues.phone ?? '',
      firstName: defaultValues.firstName ?? '',
      lastName: defaultValues.lastName ?? '',
      dateOfBirth: defaultValues.dateOfBirth ?? '',
      zipCode: defaultValues.zipCode ?? '',
      mrn: defaultValues.mrn ?? '',
      invite: firstInviteLabelKey ?? '',
    },
    validators: {
      onMount: patientInformationSchema,
      onChange: patientInformationSchema,
    },
    onSubmit: ({ value }) => {
      onSubmit(value);
    },
  });

  const isNextDisabled = useStore(
    form.store,
    (state) => !state.canSubmit || state.isSubmitting,
  );

  useEffect(() => {
    setIsNextDisabled(isNextDisabled);
  }, [isNextDisabled, setIsNextDisabled]);

  useImperativeHandle(
    ref,
    () => ({
      submitForm: form.handleSubmit,
    }),
    [form.handleSubmit],
  );

  return (
    <Stack gap="$4" paddingTop="$2" testID={testID}>
      <Text
        type="$bodyBase.medium"
        color="$text.100"
        paddingBottom="$2"
        testID={buildTestId({ testID, suffix: 'title' })}
      >
        Patient Information
      </Text>
      {/* Disabled row, subject to change */}
      <Stack flexDirection="row" gap="$6">
        <Stack flex={1} flexShrink={1} flexBasis="0%">
          <form.Field name="phone">
            {(field) => (
              <CLMPhoneInput
                testID={buildTestId({
                  testID,
                  suffix: ['input', 'phone'],
                })}
                label="Phone Number"
                value={field.state.value}
                onChangeText={field.handleChange}
                onBlur={field.handleBlur}
                disabled
                {...getErrorProps(field)}
              />
            )}
          </form.Field>
        </Stack>
        <Stack flex={1} flexShrink={1} flexBasis="0%">
          <form.Field name="email">
            {(field) => (
              <CLMEmailInput
                testID={buildTestId({
                  testID,
                  suffix: ['input', 'email'],
                })}
                label="Email"
                placeholder="Email"
                value={field.state.value}
                onChangeText={field.handleChange}
                onBlur={field.handleBlur}
                {...getErrorProps(field)}
              />
            )}
          </form.Field>
        </Stack>
      </Stack>

      {/* First / Last Name */}
      <Stack flexDirection="row" gap="$6">
        <Stack flex={1} flexShrink={1} flexBasis="0%">
          <form.Field name="firstName">
            {(field) => (
              <CLMTextInput
                testID={buildTestId({
                  testID,
                  suffix: ['input', 'first', 'name'],
                })}
                label="First Name"
                placeholder="First Name"
                value={field.state.value}
                onChangeText={field.handleChange}
                onBlur={field.handleBlur}
                autoCapitalize="words"
                {...getErrorProps(field)}
              />
            )}
          </form.Field>
        </Stack>
        <Stack flex={1} flexShrink={1} flexBasis="0%">
          <form.Field name="lastName">
            {(field) => (
              <CLMTextInput
                testID={buildTestId({
                  testID,
                  suffix: ['input', 'last', 'name'],
                })}
                label="Last Name"
                placeholder="Last Name"
                value={field.state.value}
                onChangeText={field.handleChange}
                onBlur={field.handleBlur}
                autoCapitalize="words"
                {...getErrorProps(field)}
              />
            )}
          </form.Field>
        </Stack>
      </Stack>

      {/* DOB / Zip */}
      <Stack flexDirection="row" gap="$6">
        <Stack flex={1} flexShrink={1} flexBasis="0%" position="relative">
          <form.Field name="dateOfBirth">
            {(field) => (
              <DatePicker
                testID={buildTestId({
                  testID,
                  suffix: ['input', 'date', 'of', 'birth'],
                })}
                defaultValue={LocalDate.now().minusYears(18)}
                label="Date of Birth"
                value={field.state.value}
                onChangeText={field.handleChange}
                onBlur={field.handleBlur}
                placeholder={DEFAULT_DATE_FORMAT}
                handleChange={field.handleChange}
                {...getErrorProps(field)}
              />
            )}
          </form.Field>
        </Stack>
        <Stack flex={1} flexShrink={1} flexBasis="0%">
          <form.Field name="zipCode">
            {(field) => (
              <CLMTextInput
                testID={buildTestId({
                  testID,
                  suffix: ['input', 'zip', 'code'],
                })}
                label="Patient ZIP Code (optional)"
                placeholder="Zip"
                value={field.state.value}
                onChangeText={field.handleChange}
                onBlur={field.handleBlur}
                {...getErrorProps(field)}
              />
            )}
          </form.Field>
        </Stack>
      </Stack>

      {/* MRN */}
      <Stack flexDirection="row" gap="$6">
        <Stack flex={1} flexShrink={1} flexBasis="0%">
          <form.Field name="mrn">
            {(field) => (
              <CLMTextInput
                testID={buildTestId({
                  testID,
                  suffix: ['input', 'mrn'],
                })}
                label="Medical Record Number"
                placeholder="Medical Record Number"
                value={field.state.value}
                onChangeText={field.handleChange}
                onBlur={field.handleBlur}
                {...getErrorProps(field)}
              />
            )}
          </form.Field>
        </Stack>
      </Stack>
    </Stack>
  );
};
